/**
 * AINFRA-171 — the runtime configuration schema for the four frontends.
 *
 * Every value a deployed frontend needs to know about its environment is listed here, once, with
 * the environment variable that fills it. The container writes `/config.js` from those variables
 * when it starts, so one image runs in every environment.
 *
 * Dev-only switches (`VITE_DEV_AUTH_BYPASS`, `VITE_DEV_UNLOCK_ADMIN_FEATURES`, the dev proxies,
 * `VITE_ACCOUNT_RETURN_DEV_ORIGINS`, `VITE_AUTH0_DEBUG`) are deliberately NOT here: they stay
 * build-time and are compiled out of every `vite build`. A key that is not in this list is rejected
 * by the reader, so runtime configuration can never switch them on.
 */
export const ENVIRONMENTS = ['development', 'staging', 'production'];
export const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'silent'];
/** Order matters: it is the key order of the rendered `config.js`. */
export const RUNTIME_CONFIG_FIELDS = [
    { key: 'environment', envVar: 'VITE_ENVIRONMENT', kind: 'environment', requiredInDeploy: true },
    { key: 'auth0Domain', envVar: 'VITE_AUTH0_DOMAIN', kind: 'hostname', requiredInDeploy: true },
    { key: 'auth0ClientId', envVar: 'VITE_AUTH0_CLIENT_ID', kind: 'id', requiredInDeploy: true },
    { key: 'auth0Audience', envVar: 'VITE_AUTH0_AUDIENCE', kind: 'url', requiredInDeploy: false },
    { key: 'koraApiUrl', envVar: 'VITE_KORA_API_URL', kind: 'url', requiredInDeploy: false },
    { key: 'koraWebUrl', envVar: 'VITE_KORA_WEB_URL', kind: 'url', requiredInDeploy: false },
    {
        key: 'secretOrgApiUrl',
        envVar: 'VITE_SECRET_ORG_API_URL',
        kind: 'url',
        requiredInDeploy: false
    },
    {
        key: 'mingleMageApiUrl',
        envVar: 'VITE_MINGLE_MAGE_API_URL',
        kind: 'url',
        requiredInDeploy: false
    },
    { key: 'atlasApiUrl', envVar: 'VITE_ATLAS_API_URL', kind: 'url', requiredInDeploy: false },
    {
        key: 'mingleMageLogLevel',
        envVar: 'VITE_MINGLE_MAGE_LOG_LEVEL',
        kind: 'logLevel',
        requiredInDeploy: false
    },
    {
        key: 'featureIreneEnabled',
        envVar: 'VITE_FEATURE_IRENE_ENABLED',
        kind: 'boolean',
        requiredInDeploy: false
    },
    {
        key: 'featureWorkspacesEnabled',
        envVar: 'VITE_FEATURE_WORKSPACES_ENABLED',
        kind: 'boolean',
        requiredInDeploy: false
    },
    {
        key: 'featureFeedLiveEnabled',
        envVar: 'VITE_FEATURE_FEED_LIVE_ENABLED',
        kind: 'boolean',
        requiredInDeploy: false
    },
    {
        key: 'featureLibraryRelatedEnabled',
        envVar: 'VITE_FEATURE_LIBRARY_RELATED_ENABLED',
        kind: 'boolean',
        requiredInDeploy: false
    },
    {
        key: 'demoAmbitiousCardEnabled',
        envVar: 'VITE_DEMO_AMBITIOUS_CARD_ENABLED',
        kind: 'boolean',
        requiredInDeploy: false
    }
];
/** The environment variable names that are runtime configuration (the ESLint rule's list). */
export const RUNTIME_CONFIG_ENV_VARS = RUNTIME_CONFIG_FIELDS.map(f => f.envVar);
/**
 * Patterns shared with the container entrypoint. Only these characters can reach `config.js`, so
 * neither the shell script nor {@link renderConfigJs} needs to escape anything.
 */
export const HOSTNAME_PATTERN = /^[A-Za-z0-9.-]+$/;
export const ID_PATTERN = /^[A-Za-z0-9._-]+$/;
export const HTTPS_URL_PATTERN = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?(\/[A-Za-z0-9._~%/-]*)?$/;
/** `http://` is accepted only when `environment` is `development` (local dev servers). */
export const HTTP_URL_PATTERN = /^http:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?(\/[A-Za-z0-9._~%/-]*)?$/;
/** The global `/config.js` assigns. */
export const RUNTIME_CONFIG_GLOBAL = '__ARCHITEIA_CONFIG__';
